<template>
  <div class="dashboard-view" :class="{ collapsed: sidebarCollapsed || !initialized }">
    <div class="sidebar">
      <LoadingMask :active="lockReportFilters" text="Cargando datos"/>

      <div class="toggle-button d-flex justify-content-center align-items-center" @click="toggleSidebarCollapsed">
        <i class="fas fa-caret-right" v-if="sidebarCollapsed"></i>
        <i class="fas fa-caret-left" v-else></i>
        <span>Filtros</span>
      </div>

      <div class="export-buttons d-flex animate__animated animate__fadeInDown" v-if="exportReportActions">
        <div class="exp d-flex justify-content-center align-items-center" title="Exportar reporte a PDF">
          <i class="fas fa-print"></i>
        </div>
        <div class="exp d-flex justify-content-center align-items-center"
             title="Exportar reporte a PDF"
             @click="exportReport('pdf')">
          <i class="far fa-file-pdf"></i>
          <span>PDF</span>
        </div>
        <div class="exp d-flex justify-content-center align-items-center"
             title="Exportar datos del reporte a CSV"
             @click="exportReport('csv')"
             >
          <i class="far fa-file-excel"></i>
          <span>CSV</span>
        </div>
      </div>

      <div class="filters">
        <div
          v-if="visibleFiltersElements.templateSelector"
          class="filter-item"
        >
          <label>Formulario</label>
          <TemplateSelector :selectedId="templateSelectedId" @select="selectTemplate" @loaded="templatesLoaded = true" />
        </div>

        <div class="filter-item" v-if="visibleFiltersElements.datesRangeSelector">
          <label>Periodo</label>
          <DateSelector v-model="dateRangeSelected" @input="loadGroups" :clearable="false" :editable="false" :disabled-date="notBeforeDate1Year" format="D [/] MMM [/] YYYY" range :shortcuts="getShorcutsDates()" />
        </div>

        <div class="filter-item" v-if="visibleFiltersElements.dateTaskCreatedSelector">
          <label>Fecha de carga de tareas</label>
          <DateSelector v-model="dateSelected" @input="loadGroups" :clearable="false" :editable="false" :disabled-date="notBeforeDate31Days" format="D [/] MMM [/] YYYY" />
        </div>

        <div class="filter-item" v-if="visibleFiltersElements.dateSelector30Days">
          <label>Fecha</label>
          <DateSelector v-model="dateSelected" @input="loadGroups" :clearable="false" :editable="false" :disabled-date="notBeforeDate30Days" format="D [/] MMM [/] YYYY" />
        </div>

        <div class="filter-item users-groups" v-if="visibleFiltersElements.groupSelector">
          <label>Grupo</label>
          <div class="list">
            <UserGroupItem v-for="item in userGroups" :key="item.id" :group="item" :root="true" @select="selectGroup" :opened="userGroups.length === 1" :selectedId="selectedGroupId"/>
          </div>
        </div>
      </div>
    </div>

    <div class="dashboard-container">
      <router-view v-if="loadedInitialData"/>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator'

import DateSelector from '@/components/generic/DateSelector.vue'
import TemplateSelector from '@/components/generic/TemplateSelector.vue'
import ReportSelector from '@/components/dashboard/ReportSelector.vue'
import UserGroupItem from '@/components/tracking/UserGroupItem.vue'
import LoadingMask from '@/components/generic/LoadingMask.vue'
import { Template } from '@/types/Templates'
import { TrackingUserGroup } from '@/types/Tracking'
import { DataFilters, FiltersDateFormats } from '@/types/Dashboard'
import moment from 'moment'
import { RawLocation } from 'vue-router'
import { PageRequest } from '@/types/Support'
import { GroupCatalogFilters, GroupStatus } from '@/types/catalogs/Groups'
import userInfoStore from '@/store/userInfo'
import CookiesService from '@/services/support/CookiesService'
import appConfig from '@/config'
import CatalogsService from '@/services/CatalogsService'

@Component({
  components: {
    DateSelector,
    ReportSelector,
    TemplateSelector,
    UserGroupItem,
    LoadingMask
  }
})
export default class Dashboard extends Vue {
  LIMIT_DATE = new Date()
  DATE_1_YEAR_BEFORE = moment().add(-1, 'year').toDate()
  DATE_30_DAYS_BEFORE = moment().add(-30, 'days').toDate()
  DATE_31_DAYS_BEFORE = moment().add(-31, 'days').toDate()
  sidebarCollapsed = false
  dateRangeSelected = [moment().startOf('isoWeek').toDate(), new Date()]
  dateSelected = new Date()
  templateSelected = new Template()
  templateSelectedId = 0
  userGroups: TrackingUserGroup[] = []
  selectedGroup: TrackingUserGroup = new TrackingUserGroup()
  selectedGroupId = 0
  templatesLoaded = false
  groupsLoaded = false

  initialized = false
  loadingReport = true

  dataFilters: DataFilters = new DataFilters()

  get loadedInitialData () {
    const initialData = [
      this.initialized,
      this.groupsLoaded
    ]
    if (this.visibleFiltersElements.templateSelector) {
      initialData.push(this.templatesLoaded)
    }
    return initialData.every(Boolean)
  }

  get lockReportFilters () {
    return this.$store.state.global.lockReportFilters
  }

  get visibleFiltersElements () {
    return this.$store.state.global.visibleFiltersElements
  }

  /** @returns true if the route has the name of the capacity and productivity report */
  get isCapacityAndProductivityReport () {
    return this.$route.name === 'report_cp'
  }

  /** @returns true if the route has the name of the operation report */
  get isOperationReport () {
    return this.$route.name === 'report_eg'
  }

  /** @returns true if the route has the name of the connection report */
  get isConnectionReport () {
    return this.$route.name === 'report_cx'
  }

  /**
   * Validates if the required filters are present to print the results of the report.
   * Only when the Template filter is present its value also is considered.
   * (At this moment only Conection Report doesn't required it)
   */
  get exportReportActions () {
    const validations = [
      this.selectedGroupId,
      !this.lockReportFilters
    ]
    if (this.visibleFiltersElements.templateSelector) {
      validations.push(this.templateSelectedId)
    }
    return validations.every(Boolean)
  }

  created () {
    this.initialized = false
    this.loadGroups().finally(() => {
      this.initialized = true
    })
  }

  @Watch('$route.query')
  watchQueryParams (value: string) {
    this.updateFilters()
  }

  @Watch('loadedInitialData')
  watchLoadedInitialData () {
    this.updateFilters()
    this.applySearchParams()
  }

  @Watch('dateRangeSelected')
  watchDateRangeSelected (val: any, old: any) {
    const limitDate = moment(this.dateRangeSelected[0]).add(30, 'days').endOf('days').toDate()
    if (this.dateRangeSelected[1] > limitDate) {
      this.dateRangeSelected = old
      this.$bvToast.toast('Solo puede elegir un periodo de máximo 31 días', {
        title: 'Fechas fuera de rango',
        autoHideDelay: 4000,
        toaster: 'b-toaster-top-left',
        variant: 'danger'
      })
    } else {
      this.applySearchParams()
    }
  }

  @Watch('dateSelected')
  watchDateSelected (val: any) {
    this.dateRangeSelected = [val, val]
    this.applySearchParams()
  }

  updateFilters () {
    let params = new URLSearchParams(this.$route.query as any)
    this.templateSelectedId = params.get('template') ? Number(params.get('template')) : 0
    this.selectedGroupId = params.get('group') ? Number(params.get('group')) : 0
    this.selectedGroup = this.selectedGroup.id ? this.selectedGroup : this.findGroupById(this.selectedGroupId) || new TrackingUserGroup()
    if (params.get('initDate') && params.get('endDate')) {
      this.dateRangeSelected = [
        moment(params.get('initDate') as any, FiltersDateFormats.QUERY_STRING).toDate(),
        moment(params.get('endDate') as any, FiltersDateFormats.QUERY_STRING).toDate()
      ]
    }
    if (params.get('date')) {
      this.dateSelected = moment(params.get('date') as any, FiltersDateFormats.QUERY_STRING).toDate()
    }
  }

  /**
   * @returns a PageRequest with the configurations to request the groups
   */
  preparePageGroupRequest () : PageRequest<GroupCatalogFilters> {
    let pageRequest = new PageRequest<GroupCatalogFilters>()
    let domainId = CookiesService.get(appConfig.domainIdCookieName)
    pageRequest.sort = 'name'
    if (userInfoStore.inRootDomain()) {
      pageRequest.filters.domain = domainId
    }
    const dateRange = this.generateDateRange()
    pageRequest.filters.fromToDates = dateRange
    pageRequest.filters.status = GroupStatus.ACTIVE
    return pageRequest
  }

  /**
   * Generate the date range to get the active groups
   *
   * @returns a list of two dates
   */
  generateDateRange (): Date[] {
    let dateRange: Date[] = []
    if (this.isCapacityAndProductivityReport) {
      // active groups between the range selected
      let millisecondsIn31Days = 2678399999
      let fromMilliseconds = this.dateRangeSelected[0].getTime()
      let toMilliseconds = this.dateRangeSelected[1].getTime()
      // only a date range of 31 days
      dateRange = (toMilliseconds - fromMilliseconds) <= millisecondsIn31Days
        ? [...this.dateRangeSelected]
        : []
    } else if (this.isOperationReport) {
      // active groups from the day selected to the current it
      dateRange = [new Date(this.dateSelected.getTime()), new Date()]
    } else if (this.isConnectionReport) {
      // active groups in the same day as the selected it
      dateRange = [new Date(this.dateSelected.getTime()), new Date(this.dateSelected.getTime())]
    }
    // UTC times
    if (dateRange.length) {
      const todayDate = new Date()
      // from date, set the hour as 00:00:00
      let fromDate = dateRange[0]
      dateRange[0] = new Date(fromDate.setHours(0, 0, 0, 0))
      // to date
      const toDate = dateRange[1]
      let toDateMilliseconds
      // when 'to' is today
      if (toDate.getFullYear() === todayDate.getFullYear() &&
      toDate.getMonth() === todayDate.getMonth() &&
      toDate.getDate() === todayDate.getDate()) {
        toDateMilliseconds = todayDate.getTime() // current time
      } else {
        toDateMilliseconds = dateRange[1].setHours(23, 59, 0, 0)
      }
      dateRange[1] = new Date(toDateMilliseconds)
    }
    return dateRange
  }

  /**
   * Get the groups and loaded into userGroups property
   */
  async loadGroups () {
    const pageRequest = this.preparePageGroupRequest()
    if (pageRequest.filters.fromToDates.length) {
      await CatalogsService.getGroups(pageRequest)
        .then((response) => {
          this.userGroups = response.results as TrackingUserGroup[]
          if (this.userGroups.length > 0) {
            this.groupsLoaded = true
            this.saveGroupsDictionaryInStore(this.userGroups)
          }
        })
    }
  }

  toggleSidebarCollapsed () {
    this.sidebarCollapsed = !this.sidebarCollapsed
  }

  notBeforeDate1Year (date: Date) {
    return date < this.DATE_1_YEAR_BEFORE || date > this.LIMIT_DATE
  }

  notBeforeDate30Days (date: Date) {
    return date < this.DATE_30_DAYS_BEFORE || date > this.LIMIT_DATE
  }

  notBeforeDate31Days (date: Date) {
    return date < this.DATE_31_DAYS_BEFORE || date > this.LIMIT_DATE
  }

  selectTemplate (template: Template) {
    this.templateSelected = template
    this.templateSelectedId = template.id
    this.applySearchParams()
  }

  selectGroup (group: TrackingUserGroup) {
    this.selectedGroup = group
    this.selectedGroupId = group.id
    this.applySearchParams()
  }

  applySearchParams () {
    this.dataFilters = new DataFilters()
    this.dataFilters.template = this.templateSelectedId
    this.dataFilters.templateName = this.templateSelected.label + '[v' + this.templateSelected.version + ']'
    this.dataFilters.group = this.selectedGroupId
    this.dataFilters.groupName = this.selectedGroup.name
    this.dataFilters.groupObject = this.selectedGroup
    this.dataFilters.initDate = moment(this.dateRangeSelected[0]).format(FiltersDateFormats.QUERY_STRING)
    this.dataFilters.endDate = moment(this.dateRangeSelected[1]).format(FiltersDateFormats.QUERY_STRING)

    this.$store.commit('global/updateReportsFilters', this.dataFilters)

    if (this.initialized) {
      this.$router.push({ name: this.$route.name, query: this.dataFilters.getValidParams() } as RawLocation).catch(() => {})
    }
  }

  thisWeekDates () {
    return [new Date(), new Date()]
  }

  getShorcutsDates () {
    return [
      {
        text: 'Esta semana',
        onClick: () => {
          return [ moment().startOf('isoWeek').toDate(), new Date() ]
        }
      },
      {
        text: 'Semana anterior',
        onClick: () => {
          return [
            moment().startOf('isoWeek').add(-1, 'week').toDate(),
            moment().startOf('isoWeek').add(-1, 'week').endOf('week').toDate()
          ]
        }
      },
      {
        text: 'Mes anterior',
        onClick: () => {
          return [
            moment().startOf('month').add(-1, 'month').toDate(),
            moment().startOf('month').add(-1, 'month').endOf('month').toDate()
          ]
        }
      },
      {
        text: 'Últimos 31 días',
        onClick: () => {
          return [ moment().add(-30, 'day').toDate(), new Date() ]
        }
      }
    ]
  }

  findGroupById (groupId: number, group?: TrackingUserGroup): TrackingUserGroup | null {
    let subgroups = null
    let result = null

    if (group) {
      if (group.id === groupId) {
        return group
      } else if (group.subGroups && group.subGroups.length) {
        subgroups = group.subGroups
      }
    } else {
      subgroups = this.userGroups
    }

    if (subgroups) {
      for (let it of subgroups) {
        result = this.findGroupById(groupId, it)
        if (result) {
          return result
        }
      }
    }

    return null
  }

  exportReport (reportType: string) {
    this.$root.$emit('export-report', reportType)
  }

  saveGroupsDictionaryInStore (groups: TrackingUserGroup[]) {
    let groupsDictionary = {}
    groups.forEach((it) => {
      this.addGroupToDictionary(groupsDictionary, it)
    })
    this.$store.commit('global/updateUserGroupsDictionary', groupsDictionary)
  }

  addGroupToDictionary (dictionary: any, group: TrackingUserGroup) {
    dictionary[group.id] = group.name
    if (group.subGroups && group.subGroups.length) {
      group.subGroups.forEach((it) => {
        this.addGroupToDictionary(dictionary, it)
      })
    }
  }
}
</script>
